import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Music } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const audioTypes = [
  // Indigenous & Cultural
  { value: 'indigenous_elders', label: 'Indigenous Elders & Knowledge Holders' },
  { value: 'cultural_custodians', label: 'Cultural Custodians' },
  // Scientific & Research
  { value: 'ecologists', label: 'Ecologists' },
  { value: 'botanists', label: 'Botanists' },
  { value: 'mycorrhizal', label: 'Mycorrhizal Researchers' },
  { value: 'climate_scientists', label: 'Climate Scientists' },
  { value: 'fire_ecologists', label: 'Fire Ecologists' },
  { value: 'hydrologists', label: 'Hydrologists' },
  // Practitioners & Land Workers
  { value: 'arborists', label: 'Arborists' },
  { value: 'rangers', label: 'Rangers' },
  { value: 'land_managers', label: 'Land Managers' },
  { value: 'timber_workers', label: 'Timber Workers' },
  { value: 'agroforesters', label: 'Agroforesters' },
  // Community & Humanities
  { value: 'historians', label: 'Historians' },
  { value: 'local_community', label: 'Local Community' },
  { value: 'children', label: 'Children' },
  { value: 'artists', label: 'Artists' },
  { value: 'faith_leaders', label: 'Faith Leaders' },
  { value: 'philosophers', label: 'Philosophers' },
  { value: 'poets', label: 'Poets' },
  // Policy & Advocacy
  { value: 'planners', label: 'Planners' },
  { value: 'lawyers', label: 'Lawyers' },
  { value: 'rights_advocates', label: 'Rights-of-Nature Advocates' },
];

const AddAudioModal = ({ tree, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: 'indigenous_elders',
    url: '',
    title: '',
  });
  const [loading, setLoading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Audio file must be less than 50MB');
        return;
      }
      setAudioFile(file);
      setFormData({ ...formData, url: '' }); // Clear URL if file is selected
    }
  };

  const uploadAudio = async () => {
    if (!audioFile) return { url: formData.url, s3_key: null };

    setUploadingAudio(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', audioFile);

      const response = await axios.post(
        `${API}/upload/audio`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return {
        url: response.data.url,
        s3_key: response.data.s3_key
      };
    } catch (error) {
      console.error('Error uploading audio:', error);
      toast.error('Failed to upload audio file');
      throw error;
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload audio first if file is selected
      let audioUrl = formData.url;
      let s3Key = null;
      
      if (audioFile) {
        const uploadResult = await uploadAudio();
        audioUrl = uploadResult.url;
        s3Key = uploadResult.s3_key;
      }

      if (!audioUrl) {
        toast.error('Please provide an audio URL or upload an audio file');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/trees/${tree.id}/audio`,
        {
          type: formData.type,
          url: audioUrl,
          s3_key: s3Key,
          title: formData.title || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Audio track added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding audio track:', error);
      toast.error('Failed to add audio track. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="add-audio-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-white border-b border-border px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-2xl font-heading font-bold text-primary">Add Audio Track</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="add-audio-form">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Adding audio track for:</p>
            <p className="font-medium text-primary">{tree.name}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Audio Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
              data-testid="audio-type-select"
            >
              {audioTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Audio File *
            </label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-input rounded-lg hover:border-primary transition-colors">
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {audioFile ? audioFile.name : 'Upload Audio File (Max 50MB)'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/webm,audio/ogg,audio/m4a"
                    onChange={handleAudioChange}
                    className="hidden"
                    data-testid="audio-file-input"
                  />
                </label>
              </div>
              
              <div className="text-center text-sm text-muted-foreground">or</div>
              
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="Enter audio URL (MP3, WAV, etc.)"
                disabled={audioFile !== null}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-secondary disabled:cursor-not-allowed"
                data-testid="audio-url-input"
              />
              
              {audioFile && (
                <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/30 rounded-lg">
                  <Music className="w-5 h-5 text-accent" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{audioFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAudioFile(null)}
                    className="p-1 hover:bg-destructive/10 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title (optional)
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Elder's Story"
              data-testid="audio-title-input"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-secondary transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Adding...' : 'Add Audio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAudioModal;